import React, { useState } from 'react'
import ImageUploader from '../../../components/ImageUploader'
import InputField from '../../../components/InputField'
import Modal from '../../../components/Modal'
import { Heading2Bold, Heading3Bold } from '../../../design-system/typography'
import { Button, TextButton } from '../../../design-system/utils'
import { Form, InputFields, Error, SecondaryPrizesWrapper } from './style'
import competitionsApi from '../../../api/competitions'
import { CompetitionStatus } from '../../../types/Competition'
import DropdownSelectorWrapper from '../../../components/DropdownSelectorWrapper/DropdownSelectorWrapper'
import LabeledField from '../../../types/LabeledField'
import DropdownSelector from '../../../components/DropdownSelector/DropdownSelector'
import CompetitionSecondaryPrize from '../CompetitionSecondaryPrize/CompetitionSecondaryPrize'
import { Wrapper } from '../../../components/InputField/style'

type CreateCompetitionProps = {
  onClose: () => void
  onUpdate: () => void
}

export const statusOptions = [
  {
    field: CompetitionStatus.active,
    label: 'Active',
  },
  {
    field: CompetitionStatus.archived,
    label: 'Archived',
  },
  {
    field: CompetitionStatus.inactive,
    label: 'Inactive',
  },
]

function CreateCompetition({ onClose, onUpdate }: CreateCompetitionProps) {
  const [thumbnail, setThumbnail] = useState(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startDateTime, setStartDateTime] = useState(null)
  const [endDateTime, setEndDateTime] = useState(null)
  const [mainPrize, setMainPrize] = useState('')
  const [prizeQuantity, setPrizeQuantity] = useState(null)
  const [url, setUrl] = useState('')
  const [status, setStatus] = useState<LabeledField>(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [secondaryPrizes, setSecondaryPrizes] = useState([])

  const addSecondaryPrize = () => {
    setSecondaryPrizes((prevState) => [
      ...prevState,
      {
        id: `${prevState?.length + 1}`,
        file: null,
        title: null,
        description: null,
        referralsRequired: null,
      },
    ])
  }

  const submitReward = (e) => {
    e.preventDefault()

    competitionsApi
      .createCompetition({
        file: thumbnail as File,
        title,
        description,
        startDateTime,
        endDateTime,
        status: status?.field,
        mainPrize,
        prizeQuantity,
        url,
        secondaryPrizes,
      })
      .then(() => {
        onUpdate()
        onClose()
      })
      .catch((err) => {
        if (err.response.status === 422) {
          setErrorMessage(
            'Unable to save changes. Please make sure that all fields are filled.'
          )
        } else {
          setErrorMessage(
            err?.response?.data || 'Something went wrong. Please try again.'
          )
        }
      })
  }

  return (
    <Modal onClose={onClose} width="695px">
      <Heading2Bold>Create a competition</Heading2Bold>
      <Form onSubmit={submitReward}>
        <InputFields>
          <ImageUploader setImage={setThumbnail} />
          <div>
            <InputField placeholder="Title" onChangeEvent={setTitle} />
            <Wrapper>
              <p>
                The title must always work with “the x Competition”. For example
                the competition title should be <b>TCS London Marathon</b> not{' '}
                <b>TCS London Marathon Entry</b>.{' '}
              </p>
            </Wrapper>
            <InputField
              placeholder="Description"
              onChangeEvent={setDescription}
            />
            <InputField
              placeholder="Start date"
              onChangeEvent={setStartDateTime}
              type="datetime-local"
            />

            <InputField
              placeholder="End date"
              onChangeEvent={setEndDateTime}
              type="datetime-local"
            />
            <InputField placeholder="Main Prize" onChangeEvent={setMainPrize} />
            <InputField
              placeholder="Prize Quantity"
              onChangeEvent={setPrizeQuantity}
              type="number"
            />

            <InputField placeholder="URL" onChangeEvent={setUrl} />
            <DropdownSelectorWrapper label="Status">
              <DropdownSelector
                currentSelection={status}
                options={statusOptions}
                onSelect={setStatus}
                placeholder="Select status"
              />
            </DropdownSelectorWrapper>
          </div>
        </InputFields>

        <SecondaryPrizesWrapper>
          <Heading3Bold>Secondary Prizes</Heading3Bold>

          {secondaryPrizes.map((prize) => (
            <CompetitionSecondaryPrize
              key={prize.id}
              id={prize.id}
              title={prize.title}
              description={prize.description}
              imageUrl={prize.imageUrl}
              referralsRequired={prize.referralsRequired}
              deletePrize={(id) => {
                setSecondaryPrizes((prevState) =>
                  prevState.filter((p) => p.id !== id)
                )
              }}
              update={(fieldName, value) => {
                setSecondaryPrizes((prevState) => {
                  const prizeToUpdate = prevState.find((p) => p.id === prize.id)
                  return prevState.map((p) =>
                    p.id === prize.id
                      ? { ...prizeToUpdate, [fieldName]: value }
                      : p
                  )
                })
              }}
            />
          ))}

          <TextButton secondary type="button" onClick={addSecondaryPrize}>
            + Add Secondary Prize
          </TextButton>
        </SecondaryPrizesWrapper>
        <Error>{errorMessage}</Error>
        <Button secondary type="submit">
          Create Competition
        </Button>
        <TextButton type="button" onClick={onClose}>
          Cancel
        </TextButton>
      </Form>
    </Modal>
  )
}
export default CreateCompetition
